import { DialogService } from '@shared/services/dialog.service';
import { SnackService } from '@shared/services/snack.service';
import { AdminActionsDirective } from '@app/main/admin/directives/admin-actions.directive';
import { IFaq } from '@shared/interfaces';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FaqService } from '@shared/services/faq.service';
import { TranslateService } from '@ngx-translate/core';
import { AdminService } from '../../../../admin.service';
import { AdminFaqService } from '@app/main/admin/modules/admin-faq/services/admin-faq.service';
import { TemplatedDialogResult } from '@shared/components/templated-dialog';
import { array_move } from '@shared/helpers/helpers';
import { Subscription } from 'rxjs';
import { ModuleService } from '@shared/services/module.service';

@Component({
    selector: 'app-admin-faq-list',
    templateUrl: './admin-faq-list.component.html',
    styleUrls: ['./admin-faq-list.component.scss'],
    standalone: false
})
export class AdminFaqListComponent extends AdminActionsDirective implements OnInit, OnDestroy {
    subscriptions: Subscription[] = [];

    constructor(
        private adminFaqService: AdminFaqService,
        private faqService: FaqService,
        private snackService: SnackService,
        private translateService: TranslateService,
        adminService: AdminService,
        dialogService: DialogService,
        moduleService: ModuleService
    ) {
        super(adminService, dialogService, moduleService);
    }

    get currentItem(): IFaq {
        return this.adminFaqService.currentItem;
    }

    get historyStack(): IFaq[] {
        return this.adminFaqService.historyStack;
    }

    get headline(): string {
        return (this.currentItem?.title as { [key: string]: string })?.en;
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.adminService.selectedItemChanged.subscribe(e => {
                if (e) {
                    this.adminFaqService.currentItem = e;
                }
            }),
            this.adminService.dataChanged.subscribe(items => {
                if (!this.selectedItem?.id) {
                    this.adminFaqService.currentItem = { items };
                } else {
                    const found = this.adminFaqService.getItem(items, this.selectedItem?.id);
                    if (found && (found.items || !found.content)) {
                        // Found a navigable parent item — update history and navigate to it
                        this.adminFaqService.replaceHistory(items);
                        this.adminFaqService.currentItem = this.selectedItem = found;
                    } else {
                        // Found a leaf item (or nothing) — avoid setting currentItem to a non-navigable item.
                        // Reset to root so the user sees fresh data with all FAQ entries visible.
                        this.adminFaqService.historyStack = [];
                        this.adminFaqService.currentItem = { items };
                        this.selectedItem = null;
                    }
                }
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(e => e.unsubscribe());
    }

    selectItem(item: IFaq) {
        if (!this.sortActive) {
            this.adminFaqService.selectItem(item);
            if (item.items || !item.content) {
                this.selectedItem = item;
            }
        }
    }

    goBackInList() {
        this.selectedItem = this.adminFaqService.goBack();
    }

    languageCount(item: IFaq): number {
        return Object.keys(item.title).filter(e => item.title[e]).length;
    }

    async deleteFaq(item: IFaq) {
        const dialog = this.dialogService.openDialog({
            header: this.translateService.instant('admin.faq.dialogs.delete.header', {
                title: (item.title as { [key: string]: string }).en
            }),
            message: this.translateService.instant('admin.faq.dialogs.delete.message' + (item.totalItems ? '2' : '1'), {
                title: (item.title as { [key: string]: string }).en,
                count: item.totalItems
            })
        });

        dialog.subscribe(async (e: TemplatedDialogResult) => {
            if (e?.result) {
                try {
                    const count = await this.faqService.deleteFaq(item.id);
                    this.snackService.openSnackBarSuccess(count + ' items deleted.');
                    this.goBackInFaq(this.selectedItem);
                } catch (e) {
                    this.snackService.openSnackBarError('error');
                }
            }
        });
    }

    async changeState(item: IFaq) {
        const state = item.active ? 'Deactivate' : 'Activate';
        const dialog = this.dialogService.openDialog({
            header: this.translateService.instant(`admin.faq.dialogs.state${state}.header`, {
                title: (item.title as { [key: string]: string }).en
            }),
            message: this.translateService.instant(
                `admin.faq.dialogs.state${state}.message` + (item.totalItems ? '2' : '1'),
                {
                    title: (item.title as { [key: string]: string }).en,
                    count: item.totalItems
                }
            )
        });

        dialog.subscribe(async (e: TemplatedDialogResult) => {
            if (e?.result) {
                try {
                    const items = await this.faqService.changeState(item);
                    this.snackService.openSnackBarSuccess('changed state');
                    this.goBackInFaq(this.adminFaqService.getItem(items, this.selectedItem?.id), items);
                } catch (e) {
                    console.error(e);
                    this.snackService.openSnackBarError('error');
                }
            }
        });
    }

    moveItem(item: IFaq, down = true) {
        const currentIndex = this.currentItem.items.indexOf(item);
        const newArray = array_move(this.currentItem.items, currentIndex, currentIndex + (down ? 1 : -1));
        newArray.forEach((e, i) => (e.sortIndex = i + 1));
    }

    get faqItem(): IFaq {
        return this.selectedItem;
    }
}
